import { useState, useEffect } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertTriangle,
  Clock,
  Calendar,
  Package,
  Trash2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-ZA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getDaysUntilExpiry = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(dateStr);
  expiry.setHours(0, 0, 0, 0);
  return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
};

const getUrgencyColor = (days) => {
  if (days === null) return "gray";
  if (days < 0) return "red";
  if (days <= 7) return "red";
  if (days <= 30) return "amber";
  return "green";
};

const ExpiryManagement = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [expiringSoon, setExpiringSoon] = useState(null);
  const [writeOffs, setWriteOffs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [daysFilter, setDaysFilter] = useState("30");
  
  // Write-off dialog
  const [writeOffDialog, setWriteOffDialog] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [writeOffQty, setWriteOffQty] = useState("");
  const [writeOffReason, setWriteOffReason] = useState("EXPIRED");
  const [writeOffNotes, setWriteOffNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/api/expiry/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error("Error fetching expiry dashboard:", error);
    }
  };

  const fetchExpiringSoon = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/expiry/expiring-soon`, {
        params: { days: daysFilter }
      });
      setExpiringSoon(response.data);
    } catch (error) {
      console.error("Error fetching expiring products:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWriteOffs = async () => {
    try {
      const response = await axios.get(`${API}/api/expiry/write-offs`);
      setWriteOffs(response.data);
    } catch (error) {
      console.error("Error fetching write-offs:", error);
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchExpiringSoon();
    fetchWriteOffs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchExpiringSoon();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysFilter]);

  const markExpiredBatches = async () => {
    try {
      const response = await saveWithRetry(() => axios.post(`${API}/api/expiry/mark-expired`));
      toast.success(`Marked ${response.data.updated_count} batches as expired`);
      fetchDashboard();
      fetchExpiringSoon();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to mark expired batches"));
    }
  };

  const openWriteOffDialog = (batch) => {
    setSelectedBatch(batch);
    setWriteOffQty(batch.quantity_remaining.toString());
    setWriteOffReason("EXPIRED");
    setWriteOffNotes("");
    setWriteOffDialog(true);
  };

  const handleWriteOff = async () => {
    if (!selectedBatch || !writeOffQty) return;
    
    const qty = parseFloat(writeOffQty);
    if (qty <= 0 || qty > selectedBatch.quantity_remaining) {
      toast.error("Invalid quantity");
      return;
    }
    
    setSubmitting(true);
    try {
      await saveWithRetry(() => axios.post(`${API}/api/expiry/write-off`, {
        batch_id: selectedBatch.id,
        quantity_written_off: qty,
        reason: writeOffReason,
        notes: writeOffNotes
      }));
      
      toast.success("Stock written off successfully");
      setWriteOffDialog(false);
      fetchDashboard();
      fetchExpiringSoon();
      fetchWriteOffs();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to write off stock"));
    } finally {
      setSubmitting(false);
    }
  };

  const renderBatchCard = (batch) => {
    const days = batch.days_until_expiry;
    const urgency = batch.urgency;
    
    const bgColor = {
      EXPIRED: "bg-red-50 border-red-300",
      CRITICAL: "bg-red-50 border-red-200",
      WARNING: "bg-amber-50 border-amber-200",
      UPCOMING: "bg-green-50 border-green-200"
    }[urgency] || "bg-gray-50";
    
    const textColor = {
      EXPIRED: "text-red-700",
      CRITICAL: "text-red-600",
      WARNING: "text-amber-600",
      UPCOMING: "text-green-600"
    }[urgency] || "text-gray-600";
    
    return (
      <div key={batch.id} className={`border rounded-lg p-4 ${bgColor}`}>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h4 className="font-semibold text-gray-800">{batch.product_name}</h4>
            <p className="text-sm text-gray-500">PLU: {batch.plu_code || "-"}</p>
            {batch.batch_number && (
              <p className="text-sm text-gray-500">Batch: {batch.batch_number}</p>
            )}
          </div>
          <div className="text-right">
            <span className={`text-lg font-bold ${textColor}`}>
              {days < 0 ? `${Math.abs(days)} days ago` : days === 0 ? "TODAY" : `${days} days`}
            </span>
            <p className="text-sm text-gray-500">{formatDate(batch.expiry_date)}</p>
          </div>
        </div>
        
        <div className="mt-3 flex justify-between items-center">
          <div>
            <span className="text-sm text-gray-600">Remaining: </span>
            <span className="font-semibold">{batch.quantity_remaining}</span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => openWriteOffDialog(batch)}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Write Off
            </Button>
          </div>
        </div>
        
        {batch.supplier_name && (
          <p className="text-xs text-gray-400 mt-2">Supplier: {batch.supplier_name}</p>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="expiry-management-page">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Expiry Management</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={markExpiredBatches}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Update Expired Status
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="expiring">Expiring Soon</TabsTrigger>
          <TabsTrigger value="writeoffs">Write-Off History</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <XCircle className="h-10 w-10 text-red-500" />
                  <div>
                    <p className="text-3xl font-bold text-red-700">
                      {dashboard?.expired_batches || 0}
                    </p>
                    <p className="text-sm text-red-600">Expired Batches</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-10 w-10 text-amber-500" />
                  <div>
                    <p className="text-3xl font-bold text-amber-700">
                      {dashboard?.expiring_7_days || 0}
                    </p>
                    <p className="text-sm text-amber-600">Expiring in 7 Days</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Clock className="h-10 w-10 text-blue-500" />
                  <div>
                    <p className="text-3xl font-bold text-blue-700">
                      {dashboard?.expiring_30_days || 0}
                    </p>
                    <p className="text-sm text-blue-600">Expiring in 30 Days</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-purple-200 bg-purple-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-10 w-10 text-purple-500" />
                  <div>
                    <p className="text-3xl font-bold text-purple-700">
                      {dashboard?.products_needing_attention || 0}
                    </p>
                    <p className="text-sm text-purple-600">Products Need Attention</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Attention List */}
          {dashboard?.attention_list && dashboard.attention_list.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Products Needing Attention
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard.attention_list.map((item, index) => (
                    <div 
                      key={item.product_id}
                      className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-gray-500">
                          PLU: {item.plu_code || "-"} | Warning: {item.warning_days} days
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-amber-600">
                          {item.batches_count} batch{item.batches_count !== 1 ? "es" : ""}
                        </p>
                        <p className="text-sm text-gray-500">
                          {item.total_quantity} units | Earliest: {formatDate(item.earliest_expiry)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Expiring Soon Tab */}
        <TabsContent value="expiring" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Label>Show expiring within:</Label>
              <Select value={daysFilter} onValueChange={setDaysFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 Days</SelectItem>
                  <SelectItem value="14">14 Days</SelectItem>
                  <SelectItem value="30">30 Days</SelectItem>
                  <SelectItem value="90">90 Days</SelectItem>
                  <SelectItem value="180">180 Days</SelectItem>
                  <SelectItem value="365">1 Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {expiringSoon && (
              <div className="text-sm text-gray-500">
                Total: {expiringSoon.summary.total_count} batches
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : expiringSoon ? (
            <div className="space-y-6">
              {/* Expired */}
              {expiringSoon.expired.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-red-700 mb-3 flex items-center gap-2">
                    <XCircle className="h-5 w-5" />
                    Expired ({expiringSoon.summary.expired_count})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {expiringSoon.expired.map(renderBatchCard)}
                  </div>
                </div>
              )}

              {/* Critical (0-7 days) */}
              {expiringSoon.critical.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-red-600 mb-3 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Critical - Within 7 Days ({expiringSoon.summary.critical_count})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {expiringSoon.critical.map(renderBatchCard)}
                  </div>
                </div>
              )}

              {/* Warning (8-30 days) */}
              {expiringSoon.warning.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-amber-600 mb-3 flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Warning - Within 30 Days ({expiringSoon.summary.warning_count})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {expiringSoon.warning.map(renderBatchCard)}
                  </div>
                </div>
              )}

              {/* Upcoming (31+ days) */}
              {expiringSoon.upcoming.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-green-600 mb-3 flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Upcoming ({expiringSoon.summary.upcoming_count})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {expiringSoon.upcoming.map(renderBatchCard)}
                  </div>
                </div>
              )}

              {expiringSoon.summary.total_count === 0 && (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <p className="text-gray-600">No batches expiring within the selected period</p>
                </div>
              )}
            </div>
          ) : null}
        </TabsContent>

        {/* Write-Off History Tab */}
        <TabsContent value="writeoffs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Write-Off History</span>
                {writeOffs && (
                  <span className="text-sm font-normal text-gray-500">
                    Total: {writeOffs.summary.count} write-offs | 
                    Qty: {writeOffs.summary.total_quantity.toLocaleString()} | 
                    Cost: R {writeOffs.summary.total_cost.toLocaleString()}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {writeOffs?.write_offs?.length > 0 ? (
                <div className="space-y-3">
                  {writeOffs.write_offs.map((wo) => (
                    <div 
                      key={wo.id}
                      className="flex justify-between items-center p-3 border rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{wo.product_name}</p>
                        <p className="text-sm text-gray-500">
                          PLU: {wo.plu_code || "-"} | Expiry: {formatDate(wo.expiry_date)}
                        </p>
                        <p className="text-sm text-gray-500">
                          Reason: {wo.reason} {wo.notes && `- ${wo.notes}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-red-600">
                          -{wo.quantity_written_off}
                        </p>
                        <p className="text-sm text-gray-500">
                          R {wo.total_cost.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatDate(wo.written_off_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Trash2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p>No write-offs recorded yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Write-Off Dialog */}
      <Dialog open={writeOffDialog} onOpenChange={setWriteOffDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Write Off Expired Stock</DialogTitle>
          </DialogHeader>
          
          {selectedBatch && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{selectedBatch.product_name}</p>
                <p className="text-sm text-gray-500">
                  Expiry: {formatDate(selectedBatch.expiry_date)} | 
                  Remaining: {selectedBatch.quantity_remaining}
                </p>
              </div>
              
              <div>
                <Label>Quantity to Write Off</Label>
                <Input
                  type="number"
                  value={writeOffQty}
                  onChange={(e) => setWriteOffQty(e.target.value)}
                  max={selectedBatch.quantity_remaining}
                  min="0.001"
                  step="0.001"
                />
              </div>
              
              <div>
                <Label>Reason</Label>
                <Select value={writeOffReason} onValueChange={setWriteOffReason}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EXPIRED">Expired</SelectItem>
                    <SelectItem value="DAMAGED">Damaged</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={writeOffNotes}
                  onChange={(e) => setWriteOffNotes(e.target.value)}
                  placeholder="Additional notes..."
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setWriteOffDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleWriteOff}
              disabled={submitting}
            >
              {submitting ? "Writing Off..." : "Confirm Write Off"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpiryManagement;
